import { RouteAccessLevel, UserType, type RouteConfig, getRouteConfig } from '../../config/route-config';

/**
 * RBAC Utility Functions
 * 
 * Helper functions for route-based access control
 */

/**
 * Check if a user can access a route based on their type
 */
export function canAccessRoute(
    userType: UserType | null,
    routeConfig: RouteConfig
): boolean {
    const { accessLevel } = routeConfig;

    // Public routes - anyone can access
    if (accessLevel === RouteAccessLevel.PUBLIC) {
        return true;
    }

    // Authenticated routes - any logged-in user
    if (accessLevel === RouteAccessLevel.AUTHENTICATED) {
        return userType !== null;
    }

    // Partner-only routes
    if (accessLevel === RouteAccessLevel.PARTNER_ONLY) {
        return userType === UserType.PARTNER;
    }

    // Regular user-only routes
    if (accessLevel === RouteAccessLevel.REGULAR_ONLY) {
        return userType === UserType.REGULAR;
    }

    // Default: deny access
    return false;
}

/**
 * Get the redirect path for a user who cannot access a route
 */
export function getRedirectPath(
    userType: UserType | null,
    currentPath: string
): string {
    const routeConfig = getRouteConfig(currentPath);

    if (!routeConfig) {
        // No config found - redirect to default based on user type
        return getDefaultDashboard(userType);
    }

    // If user can access, no redirect needed
    if (canAccessRoute(userType, routeConfig)) {
        return currentPath;
    }

    // Use configured redirect if available
    if (routeConfig.redirectTo) {
        if (userType === UserType.PARTNER && routeConfig.redirectTo.partner) {
            return routeConfig.redirectTo.partner;
        }
        if (userType === UserType.REGULAR && routeConfig.redirectTo.regular) {
            return routeConfig.redirectTo.regular;
        }
    }

    // Fallback to default dashboard
    return getDefaultDashboard(userType);
}

/**
 * Get the default dashboard for a user type
 */
export function getDefaultDashboard(userType: UserType | null): string {
    if (userType === UserType.PARTNER) {
        return '/partner/dashboard';
    }
    if (userType === UserType.REGULAR) {
        return '/home';
    }
    // Not authenticated
    return '/sign-in';
}

/**
 * Get user type from profile data
 */
export function getUserTypeFromProfile(
    profile: { user_type?: string } | null
): UserType | null {
    if (!profile || !profile.user_type) {
        return null;
    }

    return profile.user_type === 'partner' ? UserType.PARTNER : UserType.REGULAR;
}

/**
 * Check if a route is allowed for a specific user type
 */
export function isRouteAllowedForUserType(
    accessLevel: RouteAccessLevel,
    userType: UserType | null
): boolean {
    switch (accessLevel) {
        case RouteAccessLevel.PUBLIC:
            return true;

        case RouteAccessLevel.AUTHENTICATED:
            return userType !== null;

        case RouteAccessLevel.PARTNER_ONLY:
            return userType === UserType.PARTNER;

        case RouteAccessLevel.REGULAR_ONLY:
            return userType === UserType.REGULAR;

        default:
            return false;
    }
}

/**
 * Get user-friendly message for unauthorized access
 */
export function getUnauthorizedMessage(
    userType: UserType | null,
    accessLevel: RouteAccessLevel
): string {
    if (!userType) {
        return 'Please sign in to access this page.';
    }

    if (accessLevel === RouteAccessLevel.PARTNER_ONLY && userType === UserType.REGULAR) {
        return 'This page is only accessible to partner organizations.';
    }

    if (accessLevel === RouteAccessLevel.REGULAR_ONLY && userType === UserType.PARTNER) {
        return 'This page is only accessible to regular users.';
    }

    return 'You do not have permission to access this page.';
}

/**
 * Log unauthorized access attempt for security monitoring
 */
export function logUnauthorizedAccess(
    userId: string | undefined,
    userType: UserType | null,
    attemptedPath: string,
    accessLevel: RouteAccessLevel
): void {

    // TODO: Send to analytics/monitoring service
    // Example: analytics.track('unauthorized_access', { ... });
}
